import { z } from 'zod';

/**
 * Demande de démo (formulaire public de la landing page).
 *
 * Le schéma est partagé entre :
 *  - le frontend (validation react-hook-form du formulaire public),
 *  - le backend (validation de `POST /demo-requests`).
 */

/** Secteurs couverts (alignés sur le bandeau « Conçu pour » de la landing). */
export const DEMO_SECTORS = [
  'sante',
  'industrie',
  'tertiaire',
  'hotellerie',
  'collectivites',
] as const;
export type DemoSector = (typeof DEMO_SECTORS)[number];

export const DEMO_SECTOR_LABELS: Record<DemoSector, string> = {
  sante: 'Santé',
  industrie: 'Industrie',
  tertiaire: 'Tertiaire',
  hotellerie: 'Hôtellerie',
  collectivites: 'Collectivités',
};

/** Statuts du suivi commercial d'une demande (côté super-admin). */
export const DEMO_REQUEST_STATUSES = [
  'nouveau',
  'contacte',
  'qualifie',
  'converti',
  'clos',
] as const;
export type DemoRequestStatus = (typeof DEMO_REQUEST_STATUSES)[number];

export const DEMO_REQUEST_STATUS_LABELS: Record<DemoRequestStatus, string> = {
  nouveau: 'Nouveau',
  contacte: 'Contacté',
  qualifie: 'Qualifié',
  converti: 'Converti',
  clos: 'Clos',
};

/** Champ optionnel : convertit "" → undefined avant validation. */
const optionalText = (max: number) =>
  z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().trim().max(max).optional(),
  );

/** US-LANDING — Soumission publique du formulaire « Demander une démo ». */
export const demoRequestSchema = z.object({
  full_name: z.string().trim().min(1, 'Nom requis').max(150),
  email: z.string().trim().email('Email invalide').max(255),
  organization: optionalText(255),
  phone: optionalText(40),
  sector: z.preprocess((v) => (v === '' ? undefined : v), z.enum(DEMO_SECTORS).optional()),
  message: optionalText(2000),
  /** Origine du formulaire (renseignée par le frontend, non saisie). */
  source: optionalText(50),
});

export type DemoRequestInput = z.infer<typeof demoRequestSchema>;

/** Mise à jour du statut de suivi (super-admin). */
export const updateDemoRequestStatusSchema = z.object({
  statut: z.enum(DEMO_REQUEST_STATUSES),
});

export type UpdateDemoRequestStatusInput = z.infer<typeof updateDemoRequestStatusSchema>;
